import { Box, Button, Link, styled } from '@material-ui/core';

import { ConfidenceWebsiteLink } from '@spotify/confidence-core-react';
import { LoadingOverlay } from '@spotify/confidence-core-react';

import { TransitionScreen } from '../TransitionScreen';
import { ExperimentationLoader } from './ExperimentationLoader';
import { WorkspaceSetupForm } from './WorkspaceSetupForm';
import { CreatedAccount, useCreateAccountForm } from './hooks';

const Overlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
}));

const verificationEmailHelperText = (
  <>
    Didn't receive a verification e-mail?{' '}
    <Link
      underline="always"
      href="mailto:confidence-support@spotify.com"
      target="_blank"
    >
      Contact support
    </Link>
    .
  </>
);

export const CreateAccountFormV2 = ({
  inviteId,
  onCreate,
  token,
}: {
  token?: string;
  inviteId?: string;
  onCreate: (account?: CreatedAccount) => void;
}) => {
  const form = useCreateAccountForm({ inviteId, token, onCreate });

  switch (form.screen) {
    case 'loading':
      return <LoadingOverlay />;

    case 'verify_email':
      return (
        <TransitionScreen
          title="Verify your e-mail address"
          description="We've sent you a verification email. Please click the link in the email to verify your address."
          actions={
            <Button
              variant="contained"
              color="primary"
              onClick={form.recheckEmailVerification}
            >
              I've verified my email
            </Button>
          }
          helperText={verificationEmailHelperText}
        />
      );

    case 'under_review':
      return (
        <TransitionScreen
          title="Thank you for signing up for Confidence"
          description="We are currently reviewing your account request. You will receive an email once it has been approved."
          helperText={
            <>
              Have questions?{' '}
              <Link
                underline="always"
                href="mailto:confidence-support@spotify.com"
                target="_blank"
              >
                Contact support
              </Link>
              .
            </>
          }
        />
      );

    case 'workspace_setup':
      return (
        <>
          {form.showOverlay && (
            <Overlay>
              <ExperimentationLoader
                completed={!!form.createdAccount}
                error={form.error}
                onResultDismissed={form.handleResultDismissed}
              />
            </Overlay>
          )}
          <WorkspaceSetupForm form={form} />
        </>
      );

    case 'login_verify_email':
      return (
        <div data-track-id="region.verify-email" data-track-impression>
          <TransitionScreen
            title="Verify your e&#8209;mail address"
            description="Please click on the link we sent you to verify your e&#8209;mail address."
            actions={
              <Button
                variant="contained"
                color="primary"
                onClick={form.handleLogin}
              >
                Log in
              </Button>
            }
            helperText={verificationEmailHelperText}
          />
        </div>
      );

    case 'welcome':
      return (
        <div data-track-id="region.welcome" data-track-impression>
          <TransitionScreen
            title="Welcome to Confidence"
            description="Use your work e-mail address, not a personal account."
            actions={
              <Button
                variant="contained"
                color="primary"
                onClick={form.handleLogin}
              >
                Continue
              </Button>
            }
            helperText={
              <>
                <strong>Already using Confidence in your company?</strong>
                <br />
                Ask a teammate for an invite or go to the{' '}
                <Link href="/login" underline="always">
                  login page
                </Link>{' '}
                if single sign-on is enabled.
                <Box marginTop={2}>
                  For more information about how Spotify processes your data,
                  please read our{' '}
                  <ConfidenceWebsiteLink
                    underline="always"
                    route="/privacy-policy"
                  >
                    Privacy Policy
                  </ConfidenceWebsiteLink>
                  .
                </Box>
              </>
            }
          />
        </div>
      );

    default:
      return null;
  }
};
